import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 shrink-0 whitespace-nowrap rounded-[var(--radius-button)] text-sm font-medium transition-[color,background-color,border-color,transform] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        outlinePrimary:
          "border border-primary bg-background text-primary hover:bg-primary/10 hover:text-primary",
        outlineDestructive:
          "border border-input bg-background text-destructive hover:bg-destructive/10 hover:text-destructive",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        /** Icon toolbar affordance — hover visible on white and tinted page backgrounds. */
        ghostToolbar:
          "text-foreground hover:bg-foreground/[0.06] hover:text-foreground active:bg-foreground/[0.09]",
        /** Muted foreground; use with icon/toolbar triggers. */
        ghostMuted:
          "text-muted-foreground hover:bg-accent hover:text-foreground",
        ghostDestructive:
          "text-destructive hover:bg-destructive/10 hover:text-destructive",
        /** Large bookmark-style icon affordance (`[&_svg]:size-5`). */
        ghostBookmark:
          "text-muted-foreground hover:bg-muted [&_svg]:!size-5 [&_svg]:shrink-0",
        link: "text-primary underline-offset-4 hover:underline",
        /** Read-only/disabled emphasis (e.g. “Applied”). */
        successMuted:
          "border-0 bg-success/10 text-success hover:bg-success/10 hover:text-success disabled:opacity-100 dark:bg-success/15 dark:text-success",
        /**
         * Public marketing solid CTA — inverted ink/paper (`--marketing-*`).
         * Pair with size `marketing` and `asChild` + link. Square corners by design.
         */
        marketingSolid:
          "rounded-none border border-[var(--marketing-text)] bg-[var(--marketing-text)] font-normal tracking-[0.02em] text-[var(--marketing-bg)] shadow-none hover:bg-[var(--marketing-bg)] hover:text-[var(--marketing-text)] focus-visible:ring-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--marketing-gold)]",
        /**
         * Public marketing text CTA — annotation weight, gold hover.
         * Pair with size `marketingLink` (or `marketingLinkSm` in footers).
         */
        marketingLink:
          "rounded-none bg-transparent font-normal text-[var(--marketing-text)] shadow-none hover:bg-transparent hover:text-[var(--marketing-gold)] focus-visible:ring-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--marketing-gold)]",
        /** Footer / muted marketing text links on paper. */
        marketingLinkMuted:
          "rounded-none bg-transparent font-normal text-[var(--yeshuman-ink-subtle,var(--marketing-muted))] shadow-none hover:bg-transparent hover:text-[var(--marketing-text)] focus-visible:ring-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--marketing-gold)]",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 px-3 text-xs",
        /** Segmented toggle / dense row (e.g. call list life filter). */
        segmented: "h-8 px-2.5 text-xs font-medium gap-1.5",
        /** Icon + label row with tighter gap (toolbar). */
        toolbarSm: "h-8 px-3 text-xs gap-1.5",
        lg: "h-10 px-8",
        /** Auth / marketing form submit — pair with `fullWidth`. */
        form: "h-12 px-4",
        /** Segmented view toggles (kanban/list, compact toolbar rows). */
        dense: "h-7 px-2.5 text-xs gap-1.5",
        /** Dense icon-only control (section headers, toolbars). */
        icon: "h-9 w-9",
        iconSm: "h-8 w-8",
        iconXs: "h-7 w-7 gap-0 p-0 [&_svg]:size-4",
        /** Flush-left text control (back links). */
        inline: "h-8 px-0 text-xs gap-2 justify-start w-fit [&_svg]:shrink-0",
        /** Inline text link inside form copy (e.g. sign up). */
        inlineLink: "h-auto p-0 text-sm font-semibold gap-0",
        /** Muted auxiliary link in form rows (e.g. forgot password). */
        inlineLinkAux:
          "h-auto p-0 text-xs font-normal gap-0 text-muted-foreground underline underline-offset-4 hover:text-foreground",
        xs: "h-6 px-2 text-xs gap-1 font-normal",
        /** Compact chip / filter clear. */
        chip: "h-7 px-1 text-xs gap-1 font-normal",
        /** Marketing solid CTA padding (YesHuman construction landing). */
        marketing: "h-auto min-h-11 px-[30px] py-4 text-sm",
        /** Marketing text CTA — touch target without pill padding. */
        marketingLink: "h-auto min-h-11 w-fit px-0 py-0 text-sm",
        /** Compact marketing text link (footer annotation row). */
        marketingLinkSm: "h-auto min-h-11 w-fit px-0 py-0 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Block-level width (forms, calendar trigger). */
  fullWidth?: boolean
  /** Content alignment (`start` pairs with outline pickers). */
  justify?: "center" | "start"
  /** Split-button leading segment (lifecycle primary action). */
  splitLead?: boolean
  /** Split-button chevron trigger segment. */
  splitChevron?: boolean
  /** Subtle press scale (`0.96`) for tactile Act-zone controls. */
  pressFeedback?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      fullWidth,
      justify = "center",
      splitLead,
      splitChevron,
      pressFeedback,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          justify === "start" &&
            "justify-start text-left font-normal",
          fullWidth && "w-full",
          splitLead && "rounded-r-none border-r-0",
          splitChevron && "rounded-l-none px-2",
          pressFeedback && "active:scale-[0.96]",
          className,
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
